import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, tap, throwError } from 'rxjs';
import { delay, map } from 'rxjs/operators';

import { ThemeService } from './theme.service';

const USE_MOCK_LOGIN = false;

interface AuthResponse {
  success: boolean;
  message?: string;
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  userID?: string;
  userName?: string;
  userEmail?: string;
}

interface JwtPayload {
  userID: string;
  userName: string;
  userEmail: string;
  menus: { id: string; rota: string; privileges: string[] }[];
  darkMode?: boolean;
  iat: number;
  exp: number;
}

@Injectable({ providedIn: 'root' })
export class LoginService {

  private headers = {
    'X-PO-No-Count-Pending-Requests': 'false',
    'X-PO-Screen-Lock': 'true'
  };

  // Sessao armazenada em sessionStorage (NAO sessionStorage) - decisao de
  // seguranca 2026-06-01: sessionStorage e compartilhado entre usuarios do
  // mesmo navegador e persiste apos fechar o browser, causando "sessao
  // fantasma" em estacao compartilhada (usuario B abria e herdava a sessao
  // do usuario A). sessionStorage e isolado por aba e some ao fechar.
  // Chaves de sessao limpas em conjunto no login/logout pra nunca misturar
  // identidade de usuarios diferentes.
  private static readonly SESSION_KEYS = [
    'token', 'refresh_token', 'username', 'usercode',
    'useremail', 'menus', 'expires_at'
  ];
  // Filial selecionada (mantida pelo FilialService) - limpa junto no logout
  // pra o proximo usuario nao herdar a filial do anterior.
  private static readonly FILIAL_KEY = 'portalnf_filial_cnpj';

  /** Remove todas as chaves de sessao + filial do sessionStorage. */
  clearSession(): void {
    LoginService.SESSION_KEYS.forEach(k => sessionStorage.removeItem(k));
    sessionStorage.removeItem(LoginService.FILIAL_KEY);
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private themeService: ThemeService
  ) {}

  /**
   * Codifica a senha em base64 (UTF-8 safe) antes de trafegar.
   * Nao substitui TLS nem o hash salgado server-side — apenas evita
   * que a senha apareca em claro em logs, devtools e proxies.
   */
  private encPwd(plain: string): string {
    const utf8 = unescape(encodeURIComponent(plain));
    return btoa(utf8);
  }

  login(username: string, password: string): Observable<AuthResponse> {
    if (USE_MOCK_LOGIN) {
      return this.mockLogin(username, password);
    }
    const body = {
      grant_type: 'password',
      username,
      password: this.encPwd(password),
      pwd_enc: 'b64'
    };
    return this.http.post<AuthResponse>('portalnf/auth/v1', body, { headers: this.headers })
      .pipe(tap(res => this.persistSession(res)));
  }

  refreshToken(): Observable<AuthResponse> {
    const refresh = sessionStorage.getItem('refresh_token');
    if (!refresh) {
      return throwError(() => ({ status: 401, message: 'Sem refresh_token' }));
    }
    const body = { grant_type: 'refresh_token', refresh_token: refresh };
    return this.http.post<AuthResponse>('portalnf/auth/v1', body, { headers: this.headers })
      .pipe(tap(res => this.persistSession(res)));
  }

  sendMailRecovery(data: { email: string }) {
    return this.http.post('portalnf/auth/sendreset', data, { headers: this.headers });
  }

  resetPassword(token: string, newPassword: string) {
    const body = {
      token,
      newPassword: this.encPwd(newPassword),
      pwd_enc: 'b64'
    };
    return this.http.post('portalnf/auth/reset', body, { headers: this.headers });
  }

  changePassword(oldPassword: string, newPassword: string) {
    const body = {
      oldPassword: this.encPwd(oldPassword),
      newPassword: this.encPwd(newPassword),
      pwd_enc: 'b64'
    };
    return this.http.post('portalnf/auth/changepass', body, { headers: this.headers });
  }

  logout(redirect = true) {
    this.clearSession();
    if (redirect) {
      this.router.navigate(['/login']);
    }
  }

  isLogged(): boolean {
    const token = sessionStorage.getItem('token');
    const expStr = sessionStorage.getItem('expires_at');
    if (!token || !expStr) return false;
    return Number(expStr) > Math.floor(Date.now() / 1000);
  }

  getToken(): string | null {
    return sessionStorage.getItem('token');
  }

  getUsername(): string | null {
    return sessionStorage.getItem('username');
  }

  getUserId(): string | null {
    return sessionStorage.getItem('usercode');
  }

  getUserEmail(): string | null {
    return sessionStorage.getItem('useremail');
  }

  getMenus(): { id: string; rota: string; privileges: string[] }[] {
    const raw = sessionStorage.getItem('menus');
    return raw ? JSON.parse(raw) : [];
  }

  hasPrivilege(menuId: string, privId: string): boolean {
    return this.getMenus().some(
      m => m.id === menuId && m.privileges.includes(privId)
    );
  }

  hasPrivilegeByRota(rota: string, privId: string): boolean {
    const target = (rota ?? '').trim();
    return this.getMenus().some(
      m => (m.rota ?? '').trim() === target && m.privileges.includes(privId)
    );
  }

  private persistSession(res: AuthResponse): void {
    if (!res?.access_token) return;
    // Limpa QUALQUER sessao anterior antes de gravar a nova - garante que
    // nenhuma chave do usuario anterior (menus/usercode/filial) sobreviva
    // e se misture com a identidade nova. Defesa contra bleed de sessao.
    this.clearSession();

    sessionStorage.setItem('token', res.access_token);
    if (res.refresh_token) sessionStorage.setItem('refresh_token', res.refresh_token);
    if (res.userID)        sessionStorage.setItem('usercode',  res.userID);
    if (res.userName)      sessionStorage.setItem('username',  res.userName);
    if (res.userEmail)     sessionStorage.setItem('useremail', res.userEmail);

    const payload = this.decodeJwt(res.access_token);
    if (payload) {
      sessionStorage.setItem('menus', JSON.stringify(payload.menus ?? []));
      sessionStorage.setItem('expires_at', String(payload.exp));
      if (typeof payload.darkMode === 'boolean') {
        this.themeService.applyFromLogin(payload.darkMode);
      }
    }
  }

  private decodeJwt(token: string): JwtPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const pad = b64.length % 4 ? b64 + '='.repeat(4 - (b64.length % 4)) : b64;
      return JSON.parse(atob(pad)) as JwtPayload;
    } catch {
      return null;
    }
  }

  private mockLogin(username: string, password: string): Observable<AuthResponse> {
    if (username === 'admin' && password === 'admin') {
      const res: AuthResponse = {
        success: true,
        access_token: 'mock.eyJ1c2VySUQiOiIwMDAwMDEiLCJ1c2VyTmFtZSI6IkFkbWluIiwidXNlckVtYWlsIjoiYWRtaW5AdGVzdC5jb20iLCJtZW51cyI6W10sImlhdCI6MCwiZXhwIjo5OTk5OTk5OTk5fQ.x',
        refresh_token: 'mock-refresh',
        token_type: 'Bearer',
        expires_in: 21600,
        userID: '000001',
        userName: 'Administrador Teste',
        userEmail: 'admin@test.com'
      };
      return of(res).pipe(delay(300), tap(r => this.persistSession(r)));
    }
    return throwError(() => ({
      status: 401,
      error: { message: 'Usuario ou senha invalidos' }
    })).pipe(delay(300));
  }
}
